"use client";

import { useEffect } from "react";
import { ApiError, session } from "@/lib/api";
import { settings } from "@/lib/config";

/**
 * Error tracking and visit counts of our own (Milestone 16): errors in this browser are sent to
 * the world server for admins (a few per page, without anything about who you are), and each
 * visit is counted once per tab. No tracking companies. While the game is on screen it says so
 * once a minute, so the admin sees how long players are on (asked).
 */
export function Reporter() {
  useEffect(() => {
    let sent = 0;
    const seen = new Set<string>();
    const send = (message: string, stack?: string) => {
      if (sent >= 5 || seen.has(message)) return;
      seen.add(message);
      sent++;
      const body = JSON.stringify({ source: "web", message: message.slice(0, 1000), stack: stack?.slice(0, 4000), url: location.pathname });
      void fetch(`${settings.worldServer}/errors`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    };
    const onError = (e: ErrorEvent) => send(e.message || "Error", e.error instanceof Error ? e.error.stack : `${e.filename}:${e.lineno}`);
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      // Network hiccups and cancelled requests aren't bugs.
      if (r instanceof ApiError || r?.name === "AbortError" || /Failed to fetch|NetworkError|Load failed/.test(String(r?.message ?? r))) return;
      send(r instanceof Error ? r.message : String(r), r instanceof Error ? r.stack : undefined);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    // Counted a few seconds in, so it never slows the map loading.
    const visit = setTimeout(() => {
      try {
        if (!sessionStorage.getItem("tow.visit") && !navigator.webdriver) {
          sessionStorage.setItem("tow.visit", "1");
          void fetch(`${settings.worldServer}/stats/visit`, { method: "POST" }).catch(() => {});
        }
      } catch {
        /* private mode */
      }
    }, 5000);
    const here = setInterval(() => {
      const token = session.get();
      if (!token || document.visibilityState !== "visible" || navigator.webdriver) return;
      void fetch(`${settings.worldServer}/me/here`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }, 60_000);
    return () => {
      clearTimeout(visit);
      clearInterval(here);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return null;
}
